import { Fragment } from 'react';

// Lightweight markdown renderer for Ash's chat replies. Handles the formatting
// Ash actually produces — paragraphs, **bold**, *italic*, `code`, bullet and
// numbered lists, and ## headings. Not a full markdown spec, but enough to make
// replies read cleanly instead of showing raw asterisks.

const codeStyle = {
  background: 'rgba(124,58,237,0.12)', color: '#5b21b6',
  padding: '1px 5px', borderRadius: 4, fontSize: '0.92em',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

function renderInline(text, kp) {
  const out = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`/g;
  let last = 0;
  let m;
  let n = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={`${kp}-b${n}`}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<em key={`${kp}-i${n}`}>{m[2]}</em>);
    else if (m[3] !== undefined) out.push(<code key={`${kp}-c${n}`} style={codeStyle}>{m[3]}</code>);
    last = m.index + m[0].length;
    n++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function ChatMarkdown({ text }) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) { blocks.push({ type: 'h', level: h[1].length, text: h[2] }); i++; continue; }

    if (/^\s*[-*•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*•]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    const para = [];
    while (
      i < lines.length && lines[i].trim() &&
      !/^\s*[-*•]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^#{1,3}\s+/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', text: para.join('\n') });
  }

  return (
    <>
      {blocks.map((b, bi) => {
        if (b.type === 'h') {
          const size = b.level === 1 ? 16.5 : b.level === 2 ? 15.5 : 14.5;
          return (
            <div key={bi} style={{ fontWeight: 700, fontSize: size, margin: bi === 0 ? '0 0 6px' : '12px 0 6px', color: 'var(--brand-deep)' }}>
              {renderInline(b.text, `h${bi}`)}
            </div>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul key={bi} style={{ margin: bi === 0 ? '0 0 0' : '8px 0 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {b.items.map((it, ii) => <li key={ii}>{renderInline(it, `ul${bi}-${ii}`)}</li>)}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={bi} style={{ margin: bi === 0 ? '0 0 0' : '8px 0 0', paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {b.items.map((it, ii) => <li key={ii}>{renderInline(it, `ol${bi}-${ii}`)}</li>)}
            </ol>
          );
        }
        const parts = b.text.split('\n');
        return (
          <p key={bi} style={{ margin: bi === 0 ? 0 : '8px 0 0', lineHeight: 1.55 }}>
            {parts.map((pl, pi) => (
              <Fragment key={pi}>{pi > 0 && <br />}{renderInline(pl, `p${bi}-${pi}`)}</Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}
